import React from 'react';
import { Repeat, Plus, X, Loader2, ChevronRight, Clock, Settings, Pencil, Trash2, Check, FileDown, Send, CheckCircle2, XCircle, Link as LinkIcon, Eye, Copy } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { ReglagesContrats } from './ReglagesContrats';
import { useConfirm } from '../ui/ConfirmProvider';

// Vue principale volontairement aérée (demande d'Emeni, 2026-09-02) : la liste ne montre qu'une barre de
// progression "Passage X sur Y" par abonnement. Tout le détail (historique des passages, document, actions)
// vit dans un tiroir latéral ouvert au clic — jamais dans la vue principale.
//
// Les formules et le compteur de passages viennent du module Prestations (prestation_formulas /
// prestation_formula_subscriptions, PR #16) — ce module s'y accroche pour le contrat (modèle, signature,
// résiliation) plutôt que de dupliquer un second système de formules/compteur, décision d'Emeni.

interface Subscription {
    id: string; contact_id: string; formula_subscription_id: string; template_id: string;
    status: string; engagement_months: number; start_date: string; end_date: string;
    prix_ttc_mensuel: number; payment_mode: string; signed_at?: string;
    formula_name: string; occurrences_used: number; occurrences_total: number; passages_libres?: boolean | number;
    next_appointment_date: string | null; next_appointment_time: string | null;
    public_token?: string | null; pdf_base64?: string | null;
    signed_document_storage_key?: string | null; decline_reason?: string | null; sent_at?: string | null;
}
interface CatalogItemRef { catalog_item_id: string; label: string; }
interface Occurrence {
    id: string; appointment_date: string; start_time: string; status: string; formula_occurrence_number: number | null; completed_at: string | null;
    // Prévu (devis annuel par passages) vs réalisé sur place (rendez-vous) — extra_items = ce qui a été
    // fait en plus du planning, à facturer séparément (2026-09-03, demande d'Emeni : « vrai suivi »).
    catalog_items?: CatalogItemRef[]; planned_items?: CatalogItemRef[]; extra_items?: CatalogItemRef[];
}

const STATUS_LABELS: Record<string, string> = {
    brouillon: 'Brouillon', envoye: 'Envoyé', consulte: 'Consulté', actif: 'Actif',
    resilie: 'Résilié', termine: 'Terminé', refuse: 'Refusé',
};
const STATUS_COLORS: Record<string, string> = {
    brouillon: 'bg-surface-soft text-content-muted border-border',
    envoye: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
    consulte: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
    actif: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
    resilie: 'bg-red-500/10 text-red-400 border-red-500/20',
    termine: 'bg-surface-soft text-content-muted border-border',
    refuse: 'bg-red-500/10 text-red-400 border-red-500/20',
};

function ProgressBar({ done, total }: { done: number; total: number }) {
    const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
    return (
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <div className="flex-1 h-1.5 rounded-full bg-surface-soft overflow-hidden">
                <div className="h-full bg-brand-600 rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-[11px] font-bold text-content-muted whitespace-nowrap">{done}/{total}</span>
        </div>
    );
}

// Un emplacement par passage prévu au contrat : réalisé / planifié / à planifier / en retard.
// Date cible du passage n = début du contrat + (n - 1) × durée / nombre de passages (répartition régulière).
type EtatPassage = 'realise' | 'planifie' | 'a_cloturer' | 'a_planifier' | 'en_retard';
interface PassageContrat { numero: number; etat: EtatPassage; dateCible: string; rdv?: Occurrence }
const JOUR_MS = 86400000;

function passagesDuContrat(sub: Subscription, occurrences: Occurrence[]): PassageContrat[] {
    const total = Number(sub.occurrences_total) || 0;
    if (!total || Number(sub.passages_libres)) return [];
    const debut = new Date(sub.start_date).getTime();
    const fin = new Date(sub.end_date).getTime();
    if (!Number.isFinite(debut) || !Number.isFinite(fin)) return [];
    const aujourdhui = Date.now();
    const valides = occurrences.filter((o) => !['annule', 'cancelled', 'canceled'].includes(String(o.status)));
    const parNumero = new Map<number, Occurrence>();
    valides.forEach((o) => { if (o.formula_occurrence_number) parNumero.set(o.formula_occurrence_number, o); });
    const sansNumero = valides.filter((o) => !o.formula_occurrence_number);
    const liste: PassageContrat[] = [];
    for (let n = 1; n <= total; n++) {
        let rdv = parNumero.get(n);
        if (!rdv && sansNumero.length) rdv = sansNumero.shift();
        const cible = new Date(debut + ((n - 1) * (fin - debut)) / total);
        const dateCible = cible.toISOString().slice(0, 10);
        let etat: EtatPassage;
        if (rdv && (rdv.status === 'completed' || rdv.completed_at)) etat = 'realise';
        else if (rdv) etat = new Date(rdv.appointment_date).getTime() + JOUR_MS < aujourdhui ? 'a_cloturer' : 'planifie';
        else etat = cible.getTime() + 14 * JOUR_MS < aujourdhui ? 'en_retard' : 'a_planifier';
        liste.push({ numero: n, etat, dateCible, rdv });
    }
    return liste;
}

const ETAT_PASSAGE: Record<EtatPassage, { libelle: string; classe: string }> = {
    realise: { libelle: 'Réalisé', classe: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' },
    planifie: { libelle: 'Planifié', classe: 'bg-sky-500/10 text-sky-600 border-sky-500/20' },
    a_cloturer: { libelle: 'Rendez-vous passé, à clôturer', classe: 'bg-orange-500/10 text-orange-600 border-orange-500/20' },
    a_planifier: { libelle: 'À planifier', classe: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
    en_retard: { libelle: 'En retard', classe: 'bg-red-500/10 text-red-500 border-red-500/20' },
};
const dateFr = (iso: string) => new Date(iso).toLocaleDateString('fr-FR');

function PassagesDuContrat({ passages, offerts }: { passages: PassageContrat[]; offerts: { passage_number: number; libelle: string; valeur: number; accorde: boolean }[] }) {
    return (
        <div className="space-y-1.5">
            {passages.map((p) => {
                const e = ETAT_PASSAGE[p.etat];
                const offertsDuPassage = offerts.filter((o) => o.passage_number === p.numero);
                const date = p.rdv ? dateFr(p.rdv.appointment_date) + (p.rdv.start_time ? ' à ' + p.rdv.start_time : '') : 'à prévoir autour du ' + dateFr(p.dateCible);
                return (
                    <div key={p.numero} className="p-2.5 rounded-xl bg-surface-soft border border-border space-y-1">
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-bold text-content">Passage {p.numero}</span>
                            <span className={'px-2 py-0.5 rounded-md text-[10px] font-bold border ' + e.classe}>{e.libelle}</span>
                        </div>
                        <p className="text-[11px] text-content-muted">{date}</p>
                        {offertsDuPassage.map((o, i) => (
                            <p key={i} className={'text-[11px] ' + (o.accorde ? 'text-emerald-600 font-semibold' : 'text-content-muted')}>
                                Offert : {o.libelle} ({o.valeur.toFixed(2).replace('.', ',')} €){o.accorde ? ' · accordé' : ''}
                            </p>
                        ))}
                    </div>
                );
            })}
        </div>
    );
}

interface ContractTemplate {
    id: string; name: string; formule_key: string; is_active: number;
    intro_text: string; clause_duree_renouvellement: string; clause_acces_intervention: string;
    clause_garantie: string; clauses_resiliation: string; clause_prestations_supplementaires: string;
}

// Textes par défaut — copie du repli côté serveur (CONTRACT_TEMPLATE_DEFAULTS, functions/api/[[path]].js),
// affichés directement DANS le champ (pas en simple exemple grisé) pour qu'un nouveau modèle parte d'un
// texte concret à corriger plutôt que d'une page blanche, demande d'Emeni (2026-09-02).
const CONTRACT_TEMPLATE_DEFAULTS = {
    intro_text: `Le présent contrat a pour objet la réalisation de prestations régulières d'entretien, de nettoyage, de désinfection et de protection des éléments textiles et mobiliers de l'établissement du Client.`,
    clause_duree_renouvellement: `Le contrat est conclu pour une durée initiale de {{duree_engagement}}. Il sera tacitement reconduit pour des périodes successives de même durée sauf dénonciation par lettre recommandée avec AR au moins 30 jours avant la date d'échéance.`,
    clause_acces_intervention: `Le Client s'engage à laisser les locaux accessibles aux heures convenues et à fournir l'accès à l'eau et à l'électricité nécessaires au fonctionnement des équipements de nettoyage.`,
    clause_garantie: `Toute réserve concernant une intervention doit être formulée dans un délai de 48 heures suivant la réalisation de la prestation.`,
    clauses_resiliation: `En cas de rupture anticipée du contrat par le Client avant le terme de {{duree_engagement}}, l'intégralité des remises commerciales accordées ainsi que la valeur des prestations offertes seront réajustées et facturées au tarif catalogue en vigueur. De plus, une indemnité correspondant à 50% des échéances restantes sera due.`,
    clause_prestations_supplementaires: `Toute prestation supplémentaire demandée par le Client en dehors du planning prévu par la formule souscrite sera facturée sur la prochaine échéance mensuelle, au tarif préférentiel de la formule {{formule}} lorsqu'applicable.`,
};
function emptyTemplate(): Omit<ContractTemplate, 'id'> {
    return { name: '', formule_key: '', is_active: 1, ...CONTRACT_TEMPLATE_DEFAULTS };
}

// Jetons cliquables (demande d'Emeni, 2026-09-02) : une personne non développeur ne doit jamais voir la
// syntaxe {{...}} — elle clique un libellé humain ("Nom du client"), le vrai token part se loger dans le
// texte tout seul, invisible.
const CONTRACT_TOKENS: { label: string; token: string }[] = [
    { label: 'Nom du client', token: '{{nom_client}}' },
    { label: 'Nom de votre établissement', token: '{{entreprise_nom}}' },
    { label: 'Formule souscrite', token: '{{formule}}' },
    { label: "Durée d'engagement", token: '{{duree_engagement}}' },
];

function StructuredField({ label, value, onChange, textareaRef, rows = 2 }: {
    label: string; value: string; onChange: (v: string) => void; textareaRef: React.RefObject<HTMLTextAreaElement>; rows?: number;
}) {
    const insertToken = (token: string) => {
        const el = textareaRef.current;
        if (!el) { onChange(value + token); return; }
        const start = el.selectionStart ?? value.length;
        const end = el.selectionEnd ?? value.length;
        const next = value.slice(0, start) + token + value.slice(end);
        onChange(next);
        requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + token.length; });
    };
    return (
        <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">{label}</label>
            <div className="flex flex-wrap gap-1.5">
                {CONTRACT_TOKENS.map((t) => (
                    <button key={t.token} type="button" onClick={() => insertToken(t.token)}
                        className="px-2 py-1 rounded-lg bg-surface-soft border border-border text-[11px] font-medium text-content-muted hover:text-content hover:border-brand-500 transition-colors">
                        {t.label}
                    </button>
                ))}
            </div>
            <textarea ref={textareaRef} value={value} onChange={(e) => onChange(e.target.value)} rows={rows}
                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
        </div>
    );
}

// Un vrai formulaire structuré (demande d'Emeni, 2026-09-02) : identité des parties, tableau des
// prestations, formule cochée et tableau financier sont calculés automatiquement à l'export (depuis
// billing_settings, le contact CRM et la formule souscrite) — jamais retapés ici. Seules 5 zones de texte
// restent modifiables par l'établissement, préremplies avec un texte par défaut qu'il peut entièrement
// retoucher, avec des jetons cliquables à libellé humain plutôt que la syntaxe {{...}}.
function TemplateEditor({ initial, onCancel, onSave }: { initial?: ContractTemplate; onCancel: () => void; onSave: (v: any) => Promise<void> }) {
    const [draft, setDraft] = React.useState<any>(initial || emptyTemplate());
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');
    const refIntro = React.useRef<HTMLTextAreaElement>(null);
    const refDuree = React.useRef<HTMLTextAreaElement>(null);
    const refResiliation = React.useRef<HTMLTextAreaElement>(null);
    const refAcces = React.useRef<HTMLTextAreaElement>(null);
    const refGarantie = React.useRef<HTMLTextAreaElement>(null);
    const refPrestationsSupp = React.useRef<HTMLTextAreaElement>(null);

    const setField = (key: keyof ContractTemplate) => (v: string) => setDraft((p: any) => ({ ...p, [key]: v }));

    const handleSave = async () => {
        if (!draft.name.trim()) { setError('Nom requis'); return; }
        setSaving(true); setError('');
        try { await onSave(draft); } catch (e: any) { setError(e?.message || 'Erreur'); } finally { setSaving(false); }
    };

    return (
        <div className="p-4 rounded-2xl bg-surface border border-brand-500/30 space-y-4">
            {error && <div className="text-xs text-red-400">{error}</div>}
            <input value={draft.name} onChange={(e) => setDraft((p: any) => ({ ...p, name: e.target.value }))}
                placeholder="Nom du modèle (ex: Contrat Formule Confort)" className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />

            <div className="p-3 rounded-xl bg-surface-soft/50 border border-dashed border-border space-y-1">
                <p className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Généré automatiquement (non modifiable ici)</p>
                <p className="text-xs text-content-muted">Identité du prestataire et du client, tableau des prestations et tarifs de la formule, cases formule cochées, tableau financier (HT/TVA/TTC), signature — remplis à l'export depuis les vraies données de l'abonnement.</p>
            </div>

            <StructuredField label="1. Objet du contrat" value={draft.intro_text} onChange={setField('intro_text')} textareaRef={refIntro} rows={3} />
            <StructuredField label="Durée et renouvellement" value={draft.clause_duree_renouvellement} onChange={setField('clause_duree_renouvellement')} textareaRef={refDuree} />
            <StructuredField label="Résiliation anticipée" value={draft.clauses_resiliation} onChange={setField('clauses_resiliation')} textareaRef={refResiliation} rows={3} />
            <StructuredField label="Accès et conditions d'intervention" value={draft.clause_acces_intervention} onChange={setField('clause_acces_intervention')} textareaRef={refAcces} />
            <StructuredField label="Garantie et réclamations" value={draft.clause_garantie} onChange={setField('clause_garantie')} textareaRef={refGarantie} />
            <StructuredField label="Prestations supplémentaires (hors planning)" value={draft.clause_prestations_supplementaires} onChange={setField('clause_prestations_supplementaires')} textareaRef={refPrestationsSupp} rows={3} />

            <div className="flex items-center gap-2 pt-1">
                <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold disabled:opacity-50 flex items-center gap-1.5">
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Enregistrer
                </button>
                <button onClick={onCancel} className="px-4 py-2 rounded-xl text-content-muted hover:text-content text-xs font-bold">Annuler</button>
            </div>
        </div>
    );
}

// Signature de l'établissement (demande d'Emeni, 2026-09-03 : « on doit pouvoir intégrer la signature de
// l'établissement pour que quand le contrat soit généré il y ait la signature de l'établissement ») —
// image déposée une fois, réutilisée automatiquement dans le bloc « Pour le Prestataire » de chaque
// contrat généré, jusqu'à ce qu'elle soit remplacée ou retirée ici.
function SignatureUploadCard() {
    const [url, setUrl] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');

    const load = React.useCallback(() => {
        moduleApi.getBillingSettings().then((r: any) => setUrl(r?.contract_signature_url || null)).finally(() => setLoading(false));
    }, []);
    React.useEffect(() => { load(); }, [load]);

    const persist = async (contract_signature_url: string | null) => {
        setSaving(true); setError('');
        try {
            const settings: any = await moduleApi.getBillingSettings();
            const { logo_url, default_tva_rate, default_tva_custom_rate, tva_rates, enable_cover_count, ...safe } = settings || {};
            const res: any = await moduleApi.saveBillingSettings({ ...safe, contract_signature_url });
            if (res?.error) { setError(res.error); return; }
            setUrl(contract_signature_url);
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de l\'enregistrement.');
        } finally {
            setSaving(false);
        }
    };

    // Redimensionne avant enregistrement (D1 refuse silencieusement une valeur trop volumineuse — piège déjà
    // rencontré ailleurs dans cette appli sur des images d'aide) : une signature n'a jamais besoin d'être
    // grande, PNG conservé pour garder la transparence d'une signature scannée sur fond blanc/transparent.
    const onFile = (file: File) => new Promise<void>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error);
        reader.onload = () => {
            const img = new Image();
            img.onerror = () => reject(new Error('Image invalide'));
            img.onload = () => {
                const scale = Math.min(1, 500 / Math.max(img.width, img.height));
                const w = Math.max(1, Math.round(img.width * scale));
                const h = Math.max(1, Math.round(img.height * scale));
                const canvas = document.createElement('canvas');
                canvas.width = w; canvas.height = h;
                canvas.getContext('2d')?.drawImage(img, 0, 0, w, h);
                persist(canvas.toDataURL('image/png'));
                resolve();
            };
            img.src = String(reader.result || '');
        };
        reader.readAsDataURL(file);
    }).catch((e) => setError(e?.message || 'Erreur de lecture du fichier.'));

    if (loading) return null;
    return (
        <div className="max-w-2xl p-4 rounded-2xl bg-surface border border-border space-y-2.5">
            <p className="text-sm font-bold text-content">Signature de l'établissement</p>
            <p className="text-xs text-content-muted">Intégrée automatiquement dans le bloc « Pour le Prestataire » de chaque contrat généré.</p>
            {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
            <div className="flex items-center gap-3">
                {url ? <img src={url} alt="Signature" className="h-14 max-w-[180px] object-contain border border-border rounded-lg bg-white p-1" /> : <p className="text-xs text-content-muted italic">Aucune signature déposée.</p>}
                <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-soft border border-border text-xs font-bold text-content-muted hover:text-content cursor-pointer ${saving ? 'opacity-50 pointer-events-none' : ''}`}>
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} {url ? 'Remplacer' : 'Déposer une signature'}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }} />
                </label>
                {url && <button onClick={() => persist(null)} disabled={saving} className="text-xs font-bold text-red-400 hover:underline disabled:opacity-50">Retirer</button>}
            </div>
        </div>
    );
}

function ContractTemplatesTab({ templates, onChanged }: { templates: ContractTemplate[]; onChanged: () => void }) {
    const [editing, setEditing] = React.useState<ContractTemplate | 'new' | null>(null);
    const [error, setError] = React.useState('');
    const confirmAction = useConfirm();

    const save = async (draft: any) => {
        if (draft.id) await moduleApi.updateContractTemplate(draft.id, draft);
        else await moduleApi.createContractTemplate(draft);
        setEditing(null); onChanged();
    };
    const remove = async (id: string) => {
        if (!(await confirmAction({ title: 'Supprimer le modèle de contrat ?', description: <>Le modèle sera retiré ; les contrats déjà générés avec lui ne changent pas.</>, confirmLabel: 'Supprimer le modèle' }))) return;
        try { await moduleApi.deleteContractTemplate(id); onChanged(); } catch (e: any) { setError(e?.message || 'Erreur suppression'); }
    };
    // Dupliquer (2026-09-06, Emeni) : repartir d'un modèle existant pour n'en changer qu'un détail (la
    // formule visée, une clause) sans réécrire tout le texte du contrat. La copie s'ouvre directement en
    // édition, prête à être renommée/ajustée.
    const duplicate = async (t: ContractTemplate) => {
        setError('');
        try {
            const { id, ...reste } = t;
            const res: any = await moduleApi.createContractTemplate({ ...reste, name: `${t.name} (copie)` });
            if (res?.error) { setError(res.error); return; }
            onChanged();
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la duplication');
        }
    };

    return (
        <div className="max-w-2xl space-y-3">
            {error && <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>}
            <div className="flex items-center justify-between">
                <p className="text-xs text-content-muted">Le texte du contrat utilisé lors de la création d'un abonnement, avec ses variables et sa clause de résiliation.</p>
                {editing === null && (
                    <button onClick={() => setEditing('new')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-soft border border-border text-xs font-bold text-content-muted hover:text-content shrink-0">
                        <Plus size={13} /> Ajouter
                    </button>
                )}
            </div>
            {editing === 'new' && <TemplateEditor onCancel={() => setEditing(null)} onSave={save} />}
            <div className="space-y-2">
                {templates.map((t) => editing !== 'new' && (editing as any)?.id === t.id ? (
                    <TemplateEditor key={t.id} initial={t} onCancel={() => setEditing(null)} onSave={save} />
                ) : (
                    <div key={t.id} className="flex items-center justify-between p-3 rounded-xl bg-surface-soft border border-border">
                        <p className="text-sm font-bold text-content">{t.name}</p>
                        <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => duplicate(t)} title="Dupliquer ce modèle" className="p-2 rounded-lg text-content-muted hover:text-content hover:bg-surface"><Copy size={14} /></button>
                            <button onClick={() => setEditing(t)} className="p-2 rounded-lg text-content-muted hover:text-content hover:bg-surface"><Pencil size={14} /></button>
                            <button onClick={() => remove(t.id)} className="p-2 rounded-lg text-content-muted hover:text-red-400 hover:bg-surface"><Trash2 size={14} /></button>
                        </div>
                    </div>
                ))}
                {templates.length === 0 && editing === null && <p className="text-xs text-content-muted italic">Aucun modèle pour l'instant.</p>}
            </div>
        </div>
    );
}

function EditSubscriptionForm({ sub, templates, onCancel, onSaved }: { sub: Subscription; templates: ContractTemplate[]; onCancel: () => void; onSaved: () => void }) {
    const [templateId, setTemplateId] = React.useState(sub.template_id);
    const [engagementMonths, setEngagementMonths] = React.useState(sub.engagement_months);
    const [prix, setPrix] = React.useState(String(sub.prix_ttc_mensuel));
    const [paymentMode, setPaymentMode] = React.useState(sub.payment_mode || 'mensuel');
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');

    const save = async () => {
        setSaving(true); setError('');
        try {
            const res: any = await moduleApi.updateSubscription(sub.id, { template_id: templateId, engagement_months: engagementMonths, prix_ttc_mensuel: Number(prix) || 0, payment_mode: paymentMode });
            if (res?.error) { setError(res.error); return; }
            onSaved();
        } catch (e: any) {
            setError(e?.message || 'Erreur');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-2.5">
            {error && <p className="text-xs text-red-400">{error}</p>}
            <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500">
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center gap-1.5">
                    <input type="number" min={1} value={engagementMonths} onChange={(e) => setEngagementMonths(Math.max(1, Number(e.target.value)))} className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                    <span className="text-xs text-content-muted shrink-0">mois</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <input type="number" min={0} step="0.01" value={prix} onChange={(e) => setPrix(e.target.value)} className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                    <span className="text-xs text-content-muted shrink-0">€/mois</span>
                </div>
            </div>
            <select value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)} className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500">
                <option value="mensuel">Mensualisation par prélèvement SEPA</option>
                <option value="a_intervention">Paiement à l'intervention</option>
            </select>
            <div className="flex items-center gap-2 pt-1">
                <button onClick={save} disabled={saving} className="px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold disabled:opacity-50 flex items-center gap-1.5">
                    {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Enregistrer
                </button>
                <button onClick={onCancel} className="px-3 py-1.5 rounded-lg text-content-muted hover:text-content text-xs font-bold">Annuler</button>
            </div>
        </div>
    );
}

// Dupliquer un contrat (2026-09-06, Emeni : "juste pour changer la formule dessus mais laisser le reste")
// — reprend modèle, engagement, tarif et mode de paiement du contrat source, seul le rattachement à la
// formule change. La souscription de formule correspondante est réutilisée si le client en a déjà une pour
// cette formule, sinon créée (elle naîtra 'en_attente_signature', ce module implique le module Contrats —
// cf. POST /crm/contacts/:id/formula-subscriptions). Le nouveau contrat démarre en brouillon comme tout
// contrat créé par POST /subscriptions, à envoyer puis signer normalement.
function DuplicateSubscriptionModal({ sub, contactLabel, onClose, onDone }: {
    sub: Subscription; contactLabel: string; onClose: () => void; onDone: () => void;
}) {
    const [formulas, setFormulas] = React.useState<any[]>([]);
    const [formulaId, setFormulaId] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        moduleApi.getPrestationFormulas()
            .then((r: any) => setFormulas((Array.isArray(r) ? r : []).filter((f: any) => f.is_active)))
            .catch(() => setFormulas([]));
    }, []);

    const dupliquer = async () => {
        if (!formulaId) return;
        setSaving(true); setError('');
        try {
            const existantes: any = await moduleApi.getContactFormulaSubscriptions(sub.contact_id);
            const dejaSouscrite = (Array.isArray(existantes) ? existantes : [])
                .find((s: any) => s.formula_id === formulaId && s.status !== 'annulee');
            let formulaSubscriptionId = dejaSouscrite?.id;
            if (!formulaSubscriptionId) {
                const creation: any = await moduleApi.createContactFormulaSubscription(sub.contact_id, { formula_id: formulaId });
                if (creation?.error) { setError(creation.error); return; }
                formulaSubscriptionId = creation.id;
            }
            const res: any = await moduleApi.createSubscription({
                contact_id: sub.contact_id,
                formula_subscription_id: formulaSubscriptionId,
                template_id: sub.template_id,
                engagement_months: sub.engagement_months,
                prix_ttc_mensuel: sub.prix_ttc_mensuel,
                payment_mode: sub.payment_mode,
            });
            if (res?.error) { setError(res.error); return; }
            onDone();
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la duplication.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[80] flex items-center justify-center p-4">
            <div className="w-full max-w-md bg-surface border border-border rounded-3xl shadow-2xl overflow-hidden">
                <div className="flex items-center justify-between p-5 border-b border-border">
                    <h3 className="font-bold text-content flex items-center gap-2"><Copy size={17} /> Dupliquer le contrat</h3>
                    <button onClick={onClose} className="p-1.5 text-content-muted hover:text-content rounded-lg hover:bg-surface-soft"><X size={18} /></button>
                </div>
                <div className="p-5 space-y-4">
                    <p className="text-sm text-content-muted">
                        Nouveau contrat pour <strong className="text-content">{contactLabel}</strong> reprenant le modèle, l'engagement
                        ({sub.engagement_months} mois), le tarif ({sub.prix_ttc_mensuel.toFixed(2)} € TTC) et le mode de paiement du contrat actuel.
                    </p>
                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Nouvelle formule</label>
                        <select value={formulaId} onChange={(e) => setFormulaId(e.target.value)}
                            className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500">
                            <option value="">Choisir une formule...</option>
                            {formulas.map((f) => (
                                <option key={f.id} value={f.id}>{f.name}</option>
                            ))}
                        </select>
                        <p className="text-[11px] text-content-muted italic">Formule actuelle : {sub.formula_name}</p>
                    </div>
                    {error && <p className="text-xs text-red-400 font-semibold">{error}</p>}
                </div>
                <div className="flex justify-end gap-3 p-5 border-t border-border">
                    <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content text-sm font-bold">Annuler</button>
                    <button onClick={dupliquer} disabled={saving || !formulaId}
                        className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">
                        {saving ? 'Création...' : 'Créer le contrat'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function SubscriptionDrawer({ sub, contact, template, templates, onClose, onChanged }: {
    sub: Subscription; contact: any; template: any; templates: ContractTemplate[]; onClose: () => void; onChanged: () => void;
}) {
    const [duplicating, setDuplicating] = React.useState(false);
    const confirmAction = useConfirm();
    const [occurrences, setOccurrences] = React.useState<Occurrence[]>([]);
    // Options offertes par la formule (lues sur le devis signé) : ce qui est prévu et ce qui a déjà été accordé.
    const [offerts, setOfferts] = React.useState<{ passage_number: number; libelle: string; valeur: number; accorde: boolean }[]>([]);
    const [dossierId, setDossierId] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [exporting, setExporting] = React.useState(false);
    const [exportError, setExportError] = React.useState('');
    const [editing, setEditing] = React.useState(false);
    const [resiliating, setResiliating] = React.useState(false);
    const [resiliationInfo, setResiliationInfo] = React.useState<any>(null);
    const [sending, setSending] = React.useState(false);
    const [validating, setValidating] = React.useState(false);
    const [previewing, setPreviewing] = React.useState(false);

    React.useEffect(() => {
        moduleApi.getPrestationFormulaSubscriptionOccurrences(sub.formula_subscription_id)
            .then((rows: any) => setOccurrences(Array.isArray(rows) ? rows : []))
            .finally(() => setLoading(false));
    }, [sub.formula_subscription_id]);

    React.useEffect(() => {
        moduleApi.getSubscriptionOfferts(sub.id)
            .then((r: any) => { setOfferts(Array.isArray(r?.offerts) ? r.offerts : []); setDossierId(r?.dossier_id || null); })
            .catch(() => setOfferts([]));
    }, [sub.id]);

    const contactLabel = contact ? (contact.company_name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim()) : '—';
    const canModify = ['brouillon', 'envoye', 'consulte', 'actif'].includes(sub.status);
    const canResilier = ['actif', 'envoye', 'consulte'].includes(sub.status);

    const exportPdf = async () => {
        setExporting(true); setExportError('');
        try {
            const data: any = await moduleApi.getSubscriptionContractData(sub.id);
            if (data?.error) throw new Error(data.error);
            const { generateContractPdfFromData } = await import('./contractPdfGenerator');
            const { buildDocumentFilename } = await import('../../lib/documentFilename');
            const { pdf } = await generateContractPdfFromData(data);
            pdf.save(buildDocumentFilename(data?.prestataire?.company_name, 'CONTRAT', sub.formula_name));
        } catch (e: any) {
            setExportError(e?.message || 'Erreur export PDF');
        } finally {
            setExporting(false);
        }
    };

    const resilier = async () => {
        if (!(await confirmAction({ title: 'Résilier l\'abonnement ?', description: <>L'abonnement de {contactLabel} sera résilié : la clause de résiliation anticipée s'applique (remises réajustées et indemnité).</>, confirmLabel: 'Résilier l\'abonnement' }))) return;
        setResiliating(true); setExportError('');
        try {
            const res: any = await moduleApi.resilierSubscription(sub.id);
            if (res?.error) { setExportError(res.error); return; }
            setResiliationInfo(res);
            onChanged();
        } catch (e: any) {
            setExportError(e?.message || 'Erreur résiliation');
        } finally {
            setResiliating(false);
        }
    };

    // Aperçu du contrat avant envoi (demande d'Emeni, 2026-09-03 : « on doit avoir un aperçu du contrat
    // avant de pouvoir l'envoyer au client ») — ouvre le PDF réel dans un nouvel onglet, même document que
    // celui qui sera effectivement envoyé, sans rien écrire en base ni déclencher d'envoi.
    const apercuContrat = async () => {
        setPreviewing(true); setExportError('');
        try {
            const data: any = await moduleApi.getSubscriptionContractData(sub.id);
            if (data?.error) throw new Error(data.error);
            const { generateContractPdfFromData } = await import('./contractPdfGenerator');
            const { blob } = await generateContractPdfFromData(data);
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (e: any) {
            setExportError(e?.message || "Erreur lors de la préparation de l'aperçu");
        } finally {
            setPreviewing(false);
        }
    };

    // Envoie (ou renvoie) le contrat au client — génère le PDF côté navigateur puis l'attache à l'email,
    // même logique brouillon->envoi que le devis de Suivi de prestation.
    const envoyerContrat = async () => {
        setSending(true); setExportError('');
        try {
            const data: any = await moduleApi.getSubscriptionContractData(sub.id);
            if (data?.error) throw new Error(data.error);
            const { generateContractPdfBase64 } = await import('./contractPdfGenerator');
            const document_base64 = await generateContractPdfBase64(data);
            const res: any = await moduleApi.sendSubscription(sub.id, { document_base64 });
            if (res?.error) { setExportError(res.error); return; }
            onChanged();
        } catch (e: any) {
            setExportError(e?.message || "Erreur lors de l'envoi du contrat");
        } finally {
            setSending(false);
        }
    };

    // Aperçu du contrat SIGNÉ (2026-09-26, demande d'Emeni : « pas d'aperçu du contrat qui a été signé »)
    // — distinct d'apercuContrat ci-dessus, qui ne fait que régénérer l'original avant envoi. Celui-ci sert
    // le fichier réellement reçu (déposé ou scellé par la signature en ligne), authentifié.
    const [voirSigneBusy, setVoirSigneBusy] = React.useState(false);
    const voirContratSigne = async () => {
        setVoirSigneBusy(true); setExportError('');
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`/api/subscriptions/${sub.id}/signed-document`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error('Document introuvable.');
            const blob = await res.blob();
            window.open(URL.createObjectURL(blob), '_blank');
        } catch (e: any) {
            setExportError(e?.message || "L'aperçu n'a pas pu être chargé.");
        } finally {
            setVoirSigneBusy(false);
        }
    };

    const validerContrat = async () => {
        setValidating(true); setExportError('');
        try {
            const res: any = await moduleApi.validerSubscription(sub.id);
            if (res?.error) { setExportError(res.error); return; }
            onChanged();
        } catch (e: any) {
            setExportError(e?.message || 'Erreur lors de la validation');
        } finally {
            setValidating(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="relative w-full max-w-md h-full bg-surface border-l border-border shadow-2xl overflow-y-auto">
                <div className="sticky top-0 bg-surface border-b border-border p-4 flex items-center justify-between">
                    <div>
                        <p className="text-sm font-bold text-content">{contactLabel}</p>
                        <p className="text-xs text-content-muted">{sub.formula_name} · {template?.name || '—'}</p>
                    </div>
                    <div className="flex items-center gap-1">
                        <button onClick={exportPdf} disabled={exporting} title="Exporter le contrat en PDF"
                            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-surface-soft border border-border text-[11px] font-bold text-content-muted hover:text-content disabled:opacity-50">
                            {exporting ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />} PDF
                        </button>
                        <button onClick={onClose} className="p-2 rounded-lg text-content-muted hover:text-content hover:bg-surface-soft"><X size={16} /></button>
                    </div>
                </div>
                {exportError && <div className="mx-4 mt-3 p-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs">{exportError}</div>}
                {resiliationInfo && (
                    <div className="mx-4 mt-3 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-500 text-xs">
                        Abonnement résilié. Échéances restantes : {resiliationInfo.echeances_restantes} — indemnité estimée : {Number(resiliationInfo.indemnite_estimee).toFixed(2)} € (à facturer manuellement).
                    </div>
                )}
                <div className="p-4 space-y-6">
                    <div className="flex items-center gap-3">
                        <span className={`px-2.5 py-1 rounded-lg text-xs font-bold border ${STATUS_COLORS[sub.status] || ''}`}>{STATUS_LABELS[sub.status] || sub.status}</span>
                        <span className="text-xs text-content-muted">{sub.engagement_months} mois — {sub.start_date} → {sub.end_date}</span>
                    </div>

                    {/* Persistant, quel que soit le statut (2026-09-26, demande d'Emeni : « pas d'aperçu du
                        contrat signé depuis le compte client » — le bloc ci-dessous, lui, disparaissait dès
                        que l'abonnement passait "actif", laissant le contrat signé introuvable). */}
                    {sub.signed_document_storage_key && (
                        <button onClick={voirContratSigne} disabled={voirSigneBusy}
                            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-surface border border-border text-xs font-bold text-content hover:border-brand-500 disabled:opacity-50">
                            {voirSigneBusy ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />} Voir le contrat signé
                        </button>
                    )}

                    {/* Signature du contrat — même règle que le devis de Suivi de prestation depuis le
                        2026-09-26 : signature électronique en ligne (code à 6 chiffres, PDF scellé), avec le
                        dépôt d'une photo/scan en second recours. Validation manuelle par l'établissement
                        ensuite dans les deux cas : rien n'active l'abonnement tout seul. */}
                    {['brouillon', 'envoye', 'consulte'].includes(sub.status) && (
                        <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-2.5">
                            {sub.signed_document_storage_key ? (
                                <>
                                    <p className="text-xs font-bold text-content flex items-center gap-1.5"><CheckCircle2 size={14} className="text-green-500" /> Exemplaire signé déposé par le client — à vérifier</p>
                                    <button onClick={validerContrat} disabled={validating}
                                        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold disabled:opacity-50">
                                        {validating ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Valider et activer l'abonnement
                                    </button>
                                </>
                            ) : (
                                <>
                                    <p className="text-xs text-content-muted">{sub.sent_at ? `Contrat envoyé le ${new Date(sub.sent_at).toLocaleDateString('fr-FR')} — en attente de signature.` : "Le contrat n'a pas encore été envoyé au client."}</p>
                                    <button onClick={apercuContrat} disabled={previewing}
                                        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-surface border border-border text-xs font-bold text-content hover:border-brand-500 disabled:opacity-50">
                                        {previewing ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />} Aperçu du contrat
                                    </button>
                                    <button onClick={envoyerContrat} disabled={sending}
                                        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold disabled:opacity-50">
                                        {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} {sub.status === 'brouillon' ? 'Envoyer le contrat' : 'Renvoyer le contrat'}
                                    </button>
                                </>
                            )}
                            {sub.public_token && (
                                <a href={`/contrat/${sub.public_token}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 text-[11px] font-bold text-content-muted hover:text-content">
                                    <LinkIcon size={11} /> Voir le lien public
                                </a>
                            )}
                        </div>
                    )}

                    {sub.status === 'refuse' && sub.decline_reason && (
                        <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 space-y-1">
                            <p className="text-xs font-bold text-content flex items-center gap-1.5"><XCircle size={14} className="text-red-400" /> Contrat décliné par le client</p>
                            <p className="text-xs text-content-muted">{sub.decline_reason}</p>
                        </div>
                    )}

                    <div className="space-y-1.5">
                        <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Échéance du contrat</label>
                        <p className="text-sm text-content">{new Date(sub.end_date).toLocaleDateString('fr-FR')}</p>
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Prochaine prestation</label>
                        <p className="text-sm text-content">
                            {sub.next_appointment_date ? `${new Date(sub.next_appointment_date).toLocaleDateString('fr-FR')} à ${sub.next_appointment_time || ''}` : 'Aucune prestation planifiée'}
                        </p>
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Passages du contrat</label>
                        {Number(sub.passages_libres) ? <span className="text-xs font-bold text-content">{sub.occurrences_used} passage{sub.occurrences_used > 1 ? 's' : ''} · libres</span> : (
                            <>
                                <ProgressBar done={sub.occurrences_used} total={sub.occurrences_total} />
                                {!loading && <PassagesDuContrat passages={passagesDuContrat(sub, occurrences)} offerts={offerts} />}
                                {dossierId && sub.status === 'actif' && (
                                    <a href={'/suivi-prestation/' + dossierId + '/pipeline'} className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold">Planifier un passage depuis le dossier</a>
                                )}
                            </>
                        )}
                    </div>

                    <div className="space-y-2">
                        <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Historique</label>
                        {loading ? (
                            <Loader2 size={16} className="animate-spin text-content-muted" />
                        ) : occurrences.length === 0 ? (
                            <p className="text-xs text-content-muted italic">Aucun rendez-vous encore rattaché.</p>
                        ) : (
                            <div className="space-y-1.5">
                                {occurrences.map((o) => {
                                    const extras = o.extra_items || [];
                                    return (
                                    <div key={o.id} className="p-2.5 rounded-xl bg-surface-soft border border-border space-y-1.5">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Clock size={13} className="text-content-muted" />
                                                <span className="text-xs font-bold text-content">{o.formula_occurrence_number ? `Passage ${o.formula_occurrence_number}` : 'Rendez-vous'}</span>
                                            </div>
                                            <div className="text-right">
                                                <span className="text-xs text-content-muted">{new Date(o.appointment_date).toLocaleDateString('fr-FR')}</span>
                                                {o.status !== 'completed' && <p className="text-[10px] text-amber-500">{o.status}</p>}
                                            </div>
                                        </div>
                                        {!!o.planned_items?.length && (
                                            <p className="text-[10px] text-content-muted"><span className="font-bold">Prévu :</span> {o.planned_items.map((it) => it.label).join(', ')}</p>
                                        )}
                                        {!!o.catalog_items?.length && (
                                            <p className="text-[10px] text-content-muted"><span className="font-bold">Réalisé :</span> {o.catalog_items.map((it) => it.label).join(', ')}</p>
                                        )}
                                        {extras.length > 0 && (
                                            <p className="text-[10px] text-amber-500 font-bold">+ Extra hors planning : {extras.map((it) => it.label).join(', ')}</p>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                            <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Tarif</label>
                            {canModify && !editing && (
                                <button onClick={() => setEditing(true)} className="text-[11px] font-bold text-brand-600 dark:text-brand-400 hover:underline flex items-center gap-1"><Pencil size={11} /> Modifier</button>
                            )}
                        </div>
                        {/* En paiement à l'intervention, aucune mensualité n'existe — afficher "X € / mois"
                            dans ce mode contredisait le contrat lui-même (2026-09-06, Emeni). */}
                        {editing ? (
                            <EditSubscriptionForm sub={sub} templates={templates} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); onChanged(); }} />
                        ) : (
                            <p className="text-sm text-content">
                                {sub.payment_mode === 'a_intervention'
                                    ? `${sub.prix_ttc_mensuel.toFixed(2)} € TTC de référence — facturation après chaque passage`
                                    : `${sub.prix_ttc_mensuel.toFixed(2)} € TTC / mois — mensualisation SEPA`}
                            </p>
                        )}
                    </div>

                    {!editing && (
                        <button onClick={() => setDuplicating(true)} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-surface-soft border border-border text-content-muted text-xs font-bold hover:text-content hover:border-brand-500 transition-colors">
                            <Copy size={13} /> Dupliquer avec une autre formule
                        </button>
                    )}

                    {canResilier && !editing && (
                        <button onClick={resilier} disabled={resiliating} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-bold hover:bg-red-500/20 disabled:opacity-50">
                            {resiliating ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />} Résilier l'abonnement
                        </button>
                    )}
                </div>
            </div>
            {duplicating && (
                <DuplicateSubscriptionModal sub={sub} contactLabel={contactLabel}
                    onClose={() => setDuplicating(false)}
                    onDone={() => { setDuplicating(false); onChanged(); onClose(); }} />
            )}
        </div>
    );
}

type SortKey = 'echeance' | 'nom';

export const SubscriptionsModule: React.FC = () => {
    const [subscriptions, setSubscriptions] = React.useState<Subscription[]>([]);
    const [contacts, setContacts] = React.useState<any[]>([]);
    const [templates, setTemplates] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [selected, setSelected] = React.useState<Subscription | null>(null);
    const [error, setError] = React.useState('');
    const [sortKey, setSortKey] = React.useState<SortKey>('echeance');
    const [parametresOuverts, setParametresOuverts] = React.useState(false);

    const load = React.useCallback(() => {
        setLoading(true);
        Promise.all([moduleApi.getSubscriptions(), moduleApi.getCRMContacts(), moduleApi.getContractTemplates()])
            .then(([subs, cts, t]: any[]) => {
                setSubscriptions(Array.isArray(subs) ? subs : []);
                setContacts(Array.isArray(cts) ? cts : []);
                setTemplates(Array.isArray(t) ? t : []);
            })
            .catch((e: any) => setError(e?.message || 'Erreur chargement'))
            .finally(() => setLoading(false));
    }, []);
    React.useEffect(() => { load(); }, [load]);

    // Réagit au changement (résiliation, modification) en gardant le tiroir synchronisé avec la liste.
    React.useEffect(() => {
        if (selected) setSelected(subscriptions.find((s) => s.id === selected.id) || null);
    }, [subscriptions]); // eslint-disable-line react-hooks/exhaustive-deps

    const contactById = (id: string) => contacts.find((c) => c.id === id);
    const templateById = (id: string) => templates.find((t) => t.id === id);
    const contactLabel = (ct: any) => ct ? (ct.company_name || `${ct.first_name || ''} ${ct.last_name || ''}`.trim() || 'Client sans nom') : 'Client supprimé';

    // Alerte de carte : prochain passage non planifié (cible dépassée de 14 jours = en retard).
    const alerte = (sub: Subscription): { texte: string; retard: boolean } | null => {
        const total = Number(sub.occurrences_total) || 0;
        const faits = Number(sub.occurrences_used) || 0;
        if (!total || Number(sub.passages_libres) || sub.next_appointment_date || faits >= total) return null;
        const n = faits + 1;
        const debut = new Date(sub.start_date).getTime();
        const fin = new Date(sub.end_date).getTime();
        if (!Number.isFinite(debut) || !Number.isFinite(fin)) return null;
        const cible = debut + ((n - 1) * (fin - debut)) / total;
        const retard = cible + 14 * JOUR_MS < Date.now();
        const quand = dateFr(new Date(cible).toISOString());
        return { retard, texte: retard ? 'Passage ' + n + ' en retard (prévu vers le ' + quand + ')' : 'Passage ' + n + ' à planifier vers le ' + quand };
    };

    const sorted = [...subscriptions].sort((a, b) => sortKey === 'echeance'
        ? new Date(a.end_date || 0).getTime() - new Date(b.end_date || 0).getTime()
        : contactLabel(contactById(a.contact_id)).localeCompare(contactLabel(contactById(b.contact_id))));

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;

    return (
        <div className="space-y-6">
            <header className="flex items-start justify-between gap-3">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-content flex items-center gap-2"><Repeat size={24} /> Contrats annuels</h1>
                    <p className="text-content-muted mt-1">Les clients engagés sur un an avec une formule (Essentiel, Confort, Premium) : passages faits, passages à planifier, options offertes.</p>
                </div>
                <button
                    onClick={() => setParametresOuverts(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-surface border border-border text-content-muted hover:text-content hover:border-brand-500 transition-colors shrink-0"
                    title="Régler les contrats : fonctionnement, clauses, modèles, signature, e-mails"
                >
                    <Settings size={14} /> Réglages
                </button>
            </header>
            {parametresOuverts && <ReglagesContrats onClose={() => setParametresOuverts(false)} templates={templates} signature={<SignatureUploadCard />} modeles={<ContractTemplatesTab templates={templates} onChanged={load} />} />}

            {error && <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>}

            {(
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <p className="text-xs text-content-muted">Un contrat annuel se crée depuis <b>Dossiers clients</b>, une fois le devis signé par un client qui a choisi une formule.</p>
                    <select value={sortKey} onChange={(e) => setSortKey(e.target.value as SortKey)} className="bg-surface-soft border border-border rounded-lg px-2.5 py-1.5 text-xs text-content outline-none focus:border-brand-500 shrink-0">
                        <option value="echeance">Trier par échéance</option>
                        <option value="nom">Trier par nom du client</option>
                    </select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {sorted.map((sub) => {
                    const contact = contactById(sub.contact_id);
                    return (
                        <button key={sub.id} onClick={() => setSelected(sub)}
                            className="w-full flex flex-col gap-3 p-4 rounded-2xl bg-surface border border-border hover:border-brand-500/40 transition-colors text-left">
                            <div className="flex items-start justify-between gap-2">
                                <p className="text-sm font-bold text-content min-w-0 truncate">{contactLabel(contact)}</p>
                                <span className={`shrink-0 px-2.5 py-1 rounded-lg text-xs font-bold border ${STATUS_COLORS[sub.status] || ''}`}>{STATUS_LABELS[sub.status] || sub.status}</span>
                            </div>
                            <div className="space-y-1 text-xs text-content-muted">
                                <p><span className="text-content font-semibold">{sub.formula_name}</span> · {sub.prix_ttc_mensuel.toFixed(2)} €/mois</p>
                                <p>Échéance : {sub.end_date ? new Date(sub.end_date).toLocaleDateString('fr-FR') : '—'}</p>
                                <p>{sub.next_appointment_date ? `Prochaine prestation : ${new Date(sub.next_appointment_date).toLocaleDateString('fr-FR')}` : 'Aucune prestation planifiée'}</p>
                                {sub.status === 'actif' && alerte(sub) && <p className={'font-bold ' + (alerte(sub)!.retard ? 'text-red-500' : 'text-amber-600')}>{alerte(sub)!.texte}</p>}
                            </div>
                            <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
                                {Number(sub.passages_libres) ? <span className="text-xs font-bold text-content">{sub.occurrences_used} passage{sub.occurrences_used > 1 ? 's' : ''} · libres</span> : <ProgressBar done={sub.occurrences_used} total={sub.occurrences_total} />}
                                <ChevronRight size={16} className="text-content-muted shrink-0" />
                            </div>
                        </button>
                    );
                })}
                </div>
                {subscriptions.length === 0 && (
                    <p className="text-sm text-content-muted italic">Aucun abonnement pour l'instant — signez un devis avec une formule dans Suivi de prestation pour en créer un.</p>
                )}
            </div>
            )}

            {selected && (
                <SubscriptionDrawer sub={selected} contact={contactById(selected.contact_id)} template={templateById(selected.template_id)} templates={templates}
                    onClose={() => setSelected(null)} onChanged={load} />
            )}
        </div>
    );
};

export default SubscriptionsModule;
